/* eslint-disable max-lines */
import { PropertySchemaType } from "~/common/types";
import type { PropertySchemaAssociation_DefaultFragment } from "~/features/admin/propertySchemas";

import {
  type ColumnKind,
  type EntityCategoryInstance,
  type ForeignKeyReference,
  type RelationCategoryInstance,
  type SchemaDesignerCategoryInstance,
  type SheetColumn,
  type SheetState,
  schemaDesignerCategoryCardinalities,
  zCategoryID,
} from "./schemaDesigner/types";
import { buildSheetColumn, buildSheetColumnForeignKey } from "./sheetHelper";

// Constants for consistent positioning and ordering
const MAIN_SHEET_ORDER = 0;
const FK_COLUMN_POSITION = 0;
const UUID_COLUMN_NAME = "_UUID";

// Type guards for schema designer category instances
export const isEntityInstance = (category: SchemaDesignerCategoryInstance): category is EntityCategoryInstance =>
  category.category.type === "entity";

export const isRelationInstance = (category: SchemaDesignerCategoryInstance): category is RelationCategoryInstance =>
  category.category.type === "relation";

/**
 * Determines if the main entity is the source in a relation
 */
const isMainEntitySource = (relation: RelationCategoryInstance, mainEntityId: string): boolean =>
  relation.sourceCategory?.instanceId === mainEntityId;

/**
 * Determines if the main entity is the target in a relation
 */
const isMainEntityTarget = (relation: RelationCategoryInstance, mainEntityId: string): boolean =>
  relation.targetCategory?.instanceId === mainEntityId;

/**
 * Gets the entity on the "other" side of a relation (not the main entity)
 * @param relation - The relation to analyze
 * @param mainEntity - The main entity in the schema
 * @returns The other entity in the relation, or undefined if not found
 */
const getOtherEntity = (
  relation: RelationCategoryInstance,
  mainEntity: SchemaDesignerCategoryInstance,
): EntityCategoryInstance | undefined => {
  if (isMainEntitySource(relation, mainEntity.instanceId)) {
    return relation.targetCategory;
  }
  if (isMainEntityTarget(relation, mainEntity.instanceId)) {
    return relation.sourceCategory;
  }
  return undefined;
};

/**
 * Builds a sheet column from a property schema association
 * @param config - Configuration object containing sheet name, column kind, category, and property
 * @param options - Optional configuration including column prefix and foreign key reference
 * @returns A fully configured sheet column
 */
const buildColumnFromPropertySchemaAssociation = (
  {
    sheetName,
    columnKind,
    category,
    property,
    fkRef,
  }: {
    sheetName: string;
    columnKind: ColumnKind;
    category: SchemaDesignerCategoryInstance;
    property: PropertySchemaAssociation_DefaultFragment;
    fkRef?: ForeignKeyReference;
  },
  { columnPrefix }: { columnPrefix?: string } = {},
) => {
  const id = crypto.randomUUID();
  // Here we build the column label from the category property name.
  // columnLabel will be used as an column title in the sheet, and will be a property name of the evidences.
  const columnLabel = columnPrefix ? `${columnPrefix}.${property.name}` : property.name;

  if (columnKind === "foreignKey") {
    if (!fkRef) {
      throw new Error("fkRef is required for foreign key columns");
    }

    return buildSheetColumnForeignKey({
      id,
      sheetName,
      columnLabel,
      property,
      order: property.position,
      categoryId: zCategoryID.parse(category.category.id),
      categoryName: category.category.name,
      categoryInstanceId: category.instanceId,
      categoryInstanceLabel: category.instanceLabel,
      fkRef,
    });
  }

  return buildSheetColumn({
    id,
    sheetName,
    columnLabel,
    columnKind,
    property,
    order: property.position,
    categoryId: columnKind === "custom" ? null : zCategoryID.parse(category.category.id),
    categoryName: category.category.name,
    categoryInstanceId: category.instanceId,
    categoryInstanceLabel: category.instanceLabel,
  });
};

/**
 * Options for creating property schema associations
 */
type PropertyOptions = {
  /** Whether the property is required */
  required?: boolean;
  /** Whether the property must be unique */
  unique?: boolean;
  /** Select options for dropdown/select properties */
  selectOptions?: Array<{ label: string; value: string }>;
};

/**
 * Creates a string-type property schema association
 */
const createStringProperty = (
  name: string,
  position: number,
  options: Partial<PropertyOptions> = {},
): PropertySchemaAssociation_DefaultFragment => {
  const finalOptions = { required: true, unique: false, selectOptions: [], ...options };
  return {
    __typename: "PropertySchemaAssociations",
    description: null,
    propertyAttributeTypeId: null,
    id: "",
    name,
    propertySchema: {
      __typename: "PropertySchemas",
      id: "",
      propertyTypeId: PropertySchemaType.String,
      selectOptions: {},
      urlAllowList: null,
    },
    position,
    required: finalOptions.required,
    unique: finalOptions.unique,
  };
};

/**
 * Creates a property schema association with specified type
 */
const createProperty = (
  name: string,
  position: number,
  propertyTypeId: PropertySchemaType,
  options: Partial<PropertyOptions> = {},
): PropertySchemaAssociation_DefaultFragment => {
  const finalOptions = { required: true, unique: false, selectOptions: [], ...options };
  return {
    __typename: "PropertySchemaAssociations",
    description: null,
    propertyAttributeTypeId: null,
    id: "",
    name,
    propertySchema: {
      __typename: "PropertySchemas",
      id: "",
      propertyTypeId,
      urlAllowList: null,
      selectOptions: finalOptions.selectOptions.reduce(
        (accumulator, option) => ({ ...accumulator, [option.value]: { en: option.label } }),
        {},
      ),
    },
    position,
    required: finalOptions.required,
    unique: finalOptions.unique,
  };
};

/**
 * Rebuilds a sheet column with an updated property type
 * @param originalColumn - The column to rebuild
 * @param newType - The new property type to apply
 * @param selectOptions - Optional select options for dropdown types
 * @returns A new column with the updated type
 */
export const rebuildColumnWithUpdatedType = (
  originalColumn: SheetColumn,
  newType: PropertySchemaType,
  selectOptions?: Array<{ label: string; value: string }>,
): SheetColumn => {
  // Create new property with updated type and options
  const updatedProperty = createProperty(originalColumn.property.name, originalColumn.order, newType, {
    required: originalColumn.property.required,
    unique: originalColumn.property.unique,
    selectOptions: selectOptions || [],
  });

  // Use buildSheetColumn to rebuild the column with consistency
  if (originalColumn.columnKind === "foreignKey") {
    return buildSheetColumnForeignKey({
      id: originalColumn.columnId,
      sheetName: originalColumn.sheetName,
      columnLabel: originalColumn.columnLabel,
      property: updatedProperty,
      order: originalColumn.order,
      categoryId: originalColumn.categoryId || undefined,
      categoryName: originalColumn.categoryName || undefined,
      categoryInstanceId: originalColumn.categoryInstanceId || undefined,
      categoryInstanceLabel: originalColumn.categoryInstanceLabel || undefined,
      fkRef: originalColumn.fkRef,
    });
  }

  return buildSheetColumn({
    id: originalColumn.columnId,
    sheetName: originalColumn.sheetName,
    columnLabel: originalColumn.columnLabel,
    columnKind: originalColumn.columnKind,
    property: updatedProperty,
    order: originalColumn.order,
    categoryId: originalColumn.columnKind === "custom" ? null : originalColumn.categoryId || undefined,
    categoryName: originalColumn.categoryName || undefined,
    categoryInstanceId: originalColumn.categoryInstanceId || undefined,
    categoryInstanceLabel: originalColumn.categoryInstanceLabel || undefined,
  });
};

/**
 * Builds a foreign key column for referencing another sheet
 */
const buildForeignKeyColumn = (
  category: SchemaDesignerCategoryInstance,
  fkRef: ForeignKeyReference,
  options: { columnPrefix?: string } = {},
) =>
  buildColumnFromPropertySchemaAssociation(
    {
      sheetName: category.instanceLabel,
      columnKind: "foreignKey",
      category,
      property: createStringProperty(`${fkRef.sheetName}.${fkRef.columnLabel}`, FK_COLUMN_POSITION, {
        required: true,
        unique: false,
      }),
      fkRef,
    },
    options,
  );

/**
 * Builds a custom column for user-defined data
 */
export const buildCustomColumn = (sheetName: string, columnOrder: number) =>
  buildColumnFromPropertySchemaAssociation({
    sheetName,
    columnKind: "custom",
    category: (() => {
      const definition = {
        id: zCategoryID.parse(crypto.randomUUID()),
        name: "custom",
        type: "entity" as const,
        properties: [],
        metadata: [],
      };

      return {
        ...definition,
        instanceId: crypto.randomUUID(),
        instanceLabel: "custom",
        isMain: false,
        category: definition,
      };
    })(),
    property: createStringProperty(`${sheetName}.custom${columnOrder}`, columnOrder, {
      required: false,
      unique: false,
    }),
  });

/**
 * Builds columns for a category, including optional foreign key and data columns
 * @param category - The category to build columns for
 * @param options - Configuration options for foreign keys and prefixes
 * @returns Array of sheet columns for the category
 */
function buildColumnsFromCategory(
  category: SchemaDesignerCategoryInstance,
  { fkRef, columnPrefix }: { fkRef?: ForeignKeyReference; columnPrefix?: string } = {},
): SheetColumn[] {
  const systemColumns: SheetColumn[] = [];
  if (fkRef) {
    systemColumns.push(buildForeignKeyColumn(category, fkRef, { columnPrefix }));
  }

  const propertyColumns = category.category.properties.map((psa) =>
    buildColumnFromPropertySchemaAssociation(
      {
        sheetName: category.instanceLabel,
        columnKind: "data",
        category,
        property: psa,
      },
      { columnPrefix },
    ),
  );
  return [...systemColumns, ...propertyColumns];
}

/**
 * Builds columns for a many-to-many relation junction table
 * @param relationCategory - The relation category
 * @param fkRefs - Array of two foreign key references (source and target)
 * @returns Array of columns including both foreign keys and relation properties
 */
function buildColumnsForManyToManyRelation(
  relationCategory: RelationCategoryInstance,
  fkRefs: [ForeignKeyReference, ForeignKeyReference],
): SheetColumn[] {
  const systemColumns: SheetColumn[] = [
    buildForeignKeyColumn(relationCategory, fkRefs[0]),
    buildForeignKeyColumn(relationCategory, fkRefs[1]),
  ];

  const propertyColumns = relationCategory.category.properties.map((propertySchemaAssociation) =>
    buildColumnFromPropertySchemaAssociation({
      sheetName: relationCategory.instanceLabel,
      columnKind: "data",
      category: relationCategory,
      property: propertySchemaAssociation,
    }),
  );
  return [...systemColumns, ...propertyColumns];
}

/**
 * Plans sheets for a specific relation based on its cardinality
 * @param relation - The relation to plan sheets for
 * @param mainSheet - The main sheet in the schema
 * @param relationIndex - Index of the relation for ordering purposes
 * @returns Array of sheets for this relation, or null if relation doesn't involve main sheet
 */
function planRelation(
  relation: RelationCategoryInstance,
  mainSheet: SheetState,
  relationIndex: number,
): SheetState[] | null {
  const mainEntityId = mainSheet.category.instanceId;
  const isMainSource = isMainEntitySource(relation, mainEntityId);
  const isMainTarget = isMainEntityTarget(relation, mainEntityId);

  if (!isMainSource && !isMainTarget) {
    return null; // Relation doesn't involve main entity - skip it
  }

  const otherEntity = getOtherEntity(relation, mainSheet.category);
  if (!otherEntity) {
    // eslint-disable-next-line no-console
    console.warn("No other entity found for relation", relation);
    return null;
  }

  switch (relation.cardinality) {
    case schemaDesignerCategoryCardinalities.ONE_TO_ONE:
      return planOneToOneRelation(relation, otherEntity, mainSheet, relationIndex);
    case schemaDesignerCategoryCardinalities.ONE_TO_MANY:
      return planOneToManyRelation(relation, otherEntity, mainSheet, relationIndex, isMainSource);
    case schemaDesignerCategoryCardinalities.MANY_TO_ONE:
      return planManyToOneRelation(relation, otherEntity, mainSheet, relationIndex, isMainSource);
    case schemaDesignerCategoryCardinalities.MANY_TO_MANY:
      return planManyToManyRelation(relation, otherEntity, mainSheet, relationIndex);
    default:
      throw new Error(`Unsupported cardinality: ${relation.cardinality}`);
  }
}

/**
 * Plans sheets for a one-to-one relation
 * Creates a single relation sheet containing foreign key to main entity and other entity data
 */
function planOneToOneRelation(
  relation: RelationCategoryInstance,
  otherEntity: SchemaDesignerCategoryInstance,
  mainSheet: SheetState,
  relationIndex: number,
): SheetState[] | null {
  // For 1:1, either side can hold the FK. We'll put it in the relation sheet.
  return [createRelationSheetWithMainForeignKeyAndOtherEntity(relation, mainSheet, relationIndex, otherEntity)];
}

/**
 * Plans sheets for a one-to-many relation
 * - If main is "one" side: creates relation sheet for "many" side data
 * - If main is "many" side: modifies main sheet to include FK, creates sheet for "one" side data
 */
function planOneToManyRelation(
  relation: RelationCategoryInstance,
  otherEntity: SchemaDesignerCategoryInstance,
  mainSheet: SheetState,
  relationIndex: number,
  isMainSource: boolean,
): SheetState[] | null {
  // ONE_TO_MANY: source(1) has many targets(n)
  // The "many" side should have FK to "one" side
  if (isMainSource) {
    // Main is the "one" side, relation sheet represents the "many" side
    return [createRelationSheetWithMainForeignKeyAndOtherEntity(relation, mainSheet, relationIndex, otherEntity)];
  } else {
    // Main is the "many" side (target), add FK and relation columns:
    // FK to other entity -> entity props -> relation props
    addForeignKeyAndRelationColumnsToMainSheet(relation, mainSheet, otherEntity);

    // Create relation sheet with other entity data if other entity exists
    return otherEntity ? [createRelationSheetWithOtherEntityOnly(relation, relationIndex, otherEntity)] : [];
  }
}

/**
 * Plans sheets for a many-to-one relation
 * - If main is "many" side: modifies main sheet to include FK, creates sheet for "one" side data
 * - If main is "one" side: creates relation sheet for "many" side data
 */
function planManyToOneRelation(
  relation: RelationCategoryInstance,
  otherEntity: SchemaDesignerCategoryInstance,
  mainSheet: SheetState,
  relationIndex: number,
  isMainSource: boolean,
): SheetState[] | null {
  // MANY_TO_ONE: sources(n) belong to target(1)
  // The "many" side should have FK to "one" side
  if (isMainSource) {
    // Main is the "many" side (source), add FK and relation columns
    addForeignKeyAndRelationColumnsToMainSheet(relation, mainSheet, otherEntity);
    return otherEntity ? [createRelationSheetWithOtherEntityOnly(relation, relationIndex, otherEntity)] : [];
  } else {
    // Main is the "one" side, relation sheet represents the "many" side and has FK to main
    return [createRelationSheetWithMainForeignKeyAndOtherEntity(relation, mainSheet, relationIndex, otherEntity)];
  }
}

/**
 * Plans sheets for a many-to-many relation
 * Creates two sheets:
 * 1. Junction table with foreign keys to both entities and relation properties
 * 2. Other entity sheet for data input
 */
function planManyToManyRelation(
  relation: RelationCategoryInstance,
  otherEntity: SchemaDesignerCategoryInstance,
  mainSheet: SheetState,
  relationIndex: number,
): SheetState[] | null {
  // Create junction table with foreign keys to both entities
  // Name format: {rel}
  const junctionSheet: SheetState = {
    name: relation.instanceLabel,
    kind: "relation",
    category: relation,
    columns: buildColumnsForManyToManyRelation(relation, [
      {
        sheetName: mainSheet.category.instanceLabel,
        columnLabel: UUID_COLUMN_NAME,
      },
      {
        sheetName: otherEntity.instanceLabel,
        columnLabel: UUID_COLUMN_NAME,
      },
    ]),
    order: relationIndex, // Will be re-assigned in planSheets
  };

  // Create a sheet for the other entity to allow data input
  // Name format: {other}
  const otherEntitySheet: SheetState = {
    name: otherEntity.instanceLabel,
    kind: "relation",
    category: relation,
    columns: buildColumnsFromCategory(otherEntity),
    order: relationIndex, // Will be re-assigned in planSheets
  };

  return [junctionSheet, otherEntitySheet];
}

// ===== Helper functions for relation planning =====

/**
 * Creates a relation sheet with foreign key to main entity and other entity data
 */
function createRelationSheetWithMainForeignKeyAndOtherEntity(
  relation: RelationCategoryInstance,
  mainSheet: SheetState,
  sheetOrder: number,
  otherEntity?: SchemaDesignerCategoryInstance | null,
): SheetState {
  return {
    name: relation.instanceLabel,
    kind: "relation",
    category: relation,
    columns: [
      ...buildColumnsFromCategory(relation, {
        fkRef: {
          sheetName: mainSheet.category.instanceLabel,
          columnLabel: UUID_COLUMN_NAME,
        },
      }),
      ...(otherEntity ? buildColumnsFromCategory(otherEntity) : []),
    ],
    order: sheetOrder,
  };
}

/**
 * Creates a relation sheet containing only other entity data (no foreign keys)
 */
function createRelationSheetWithOtherEntityOnly(
  relation: RelationCategoryInstance,
  sheetOrder: number,
  otherEntity: SchemaDesignerCategoryInstance,
): SheetState {
  return {
    name: relation.instanceLabel,
    kind: "relation",
    category: relation,
    columns: buildColumnsFromCategory(otherEntity),
    order: sheetOrder,
  };
}

/**
 * Adds foreign key and relation property columns to the main sheet
 * when main entity is on the "many" side of a relation.
 * Uses incremental addition to preserve existing columns from other relations.
 */
function addForeignKeyAndRelationColumnsToMainSheet(
  relation: RelationCategoryInstance,
  mainSheet: SheetState,
  otherEntity: SchemaDesignerCategoryInstance,
): void {
  // Build new FK column for this relation
  // property.name should include relation prefix for mapping, while the label remains unprefixed
  //
  const newForeignKeyColumn = buildForeignKeyColumn(
    mainSheet.category,
    {
      sheetName: otherEntity.instanceLabel,
      columnLabel: UUID_COLUMN_NAME,
    },
    {
      columnPrefix: relation.instanceLabel,
    },
  );

  // Build relation property columns for this relation
  const relationPropertyColumns = relation.category.properties.map((psa) =>
    buildColumnFromPropertySchemaAssociation(
      {
        sheetName: mainSheet.name,
        columnKind: "data",
        category: relation,
        property: psa,
      },
      { columnPrefix: relation.instanceLabel },
    ),
  );

  // Find where to insert the FK (after existing FKs, before data columns)
  let fkInsertIndex = 0;
  while (
    fkInsertIndex < mainSheet.columns.length &&
    mainSheet.columns[fkInsertIndex].columnKind === "foreignKey" &&
    mainSheet.columns[fkInsertIndex].order === FK_COLUMN_POSITION
  ) {
    fkInsertIndex++;
  }

  // Insert the new FK column at the appropriate position
  mainSheet.columns.splice(fkInsertIndex, 0, newForeignKeyColumn);

  // Append relation properties at the end
  mainSheet.columns.push(...relationPropertyColumns);
}

/**
 * Plans and creates all sheets for a schema design based on categories
 * @param categories - Array of schema designer categories (entities and relations)
 * @returns Array of sheet states representing the planned schema
 */
function planSheets(categories: readonly SchemaDesignerCategoryInstance[]): SheetState[] {
  const mainEntity = categories.find((category) => category.isMain) || categories.find(isEntityInstance);
  if (!mainEntity) {
    throw new Error("At least one entity category is required");
  }

  const mainSheet: SheetState = {
    name: mainEntity.instanceLabel,
    kind: "main",
    category: mainEntity,
    columns: buildColumnsFromCategory(mainEntity),
    order: MAIN_SHEET_ORDER,
  };

  // Create sheets for all relations involving the main entity
  const relations = categories.filter(isRelationInstance);
  const relationSheets = relations
    .flatMap((relation, relationIndex) => planRelation(relation, mainSheet, relationIndex))
    .filter((sheet) => sheet !== null);

  // Re-assign order values to ensure proper ordering across all relation sheets
  relationSheets.forEach((sheet, sheetIndex) => {
    sheet.order = sheetIndex;
  });

  return [mainSheet, ...relationSheets];
}

export const SheetPlanner = {
  planSheets,
};
